import Phaser from 'phaser';

import { TEXTURE_KEYS } from '@/features/letterParty/game/rendering/assets';
import LottieCanvas, { LOTTIE_TEXTURE_SCALE } from '@/features/letterParty/game/rendering/lottieCanvas';

import djAnimation from '@/features/letterParty/assets/lottie/dj-level-1.json';

import type { LetterPartyTheme } from '@/features/letterParty/types';

const BACKGROUND_WAVES_PLAYBACK_SPEED = 0.75;

type AddBoardObject = (object: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[]) => void;

export default class LetterPartyBackgroundRenderer {
	constructor(
		private readonly scene: Phaser.Scene,
		private readonly lottieRenderer: LottieCanvas,
		private readonly addBoardObject: AddBoardObject,
		private readonly theme: LetterPartyTheme,
	) {}

	public render(
		stageWidth: number,
		stageHeight: number,
		score: number,
		pixelRatio: number,
		showMovementButtons: boolean,
	) {
		const viewportWidth = stageWidth / pixelRatio;
		const viewportHeight = stageHeight / pixelRatio;
		const background = this.scene.add
			.rectangle(0, 0, stageWidth, stageHeight, this.theme.colors.gameBackground)
			.setOrigin(0)
			.setDepth(-1000);
		this.addBoardObject(background);
		const { palmTree, speaker, waves } = this.theme.animations.gameScreen;

		this.lottieRenderer.add({
			key: 'background-waves',
			signature: 'background-waves',
			x: stageWidth / 2,
			y: 0,
			width: stageWidth,
			height: Math.max(stageHeight * 0.34, stageWidth * 0.22),
			animationData: waves,
			depth: -900,
			originX: 0.5,
			originY: 0,
			isPlaying: true,
			playbackSpeed: BACKGROUND_WAVES_PLAYBACK_SPEED,
			preserveAspectRatio: 'xMidYMin meet',
		});

		const palmWidth = stageWidth * 0.3;

		if (viewportWidth > viewportHeight) {
			this.lottieRenderer.add({
				key: 'background-palm-right',
				signature: 'background-palm-right',
				x: stageWidth + stageWidth * 0.09,
				y: stageHeight * 0.2,
				width: palmWidth,
				height: palmWidth,
				animationData: palmTree,
				depth: -800,
				originX: 1,
				originY: 0,
				isFlipped: true,
				isPlaying: true,
			});
		}

		this.addImageByWidth(TEXTURE_KEYS.sandLeft, stageWidth * 0.085, stageHeight * 0.68, stageWidth * 0.07, -700);
		this.addImageByWidth(TEXTURE_KEYS.sandRight, stageWidth * 0.94, stageHeight * 0.5, stageWidth * 0.1, -700);
		this.addImageByWidth(TEXTURE_KEYS.sandBottom, stageWidth * 0.5, stageHeight * 0.94, stageWidth * 0.07, -700);

		const showDecorations = viewportWidth > viewportHeight && viewportHeight >= 768;
		if (showDecorations) {
			this.addImageByWidth(
				TEXTURE_KEYS.sandcastle,
				stageWidth * 0.115,
				stageHeight * 0.25,
				stageWidth * 0.07,
				-700,
			);

			this.addImageByHeight(
				TEXTURE_KEYS.swimmingFloat,
				stageWidth * 0.5,
				stageHeight * 0.92,
				stageHeight * 0.08,
				-600,
			);
		}
		const minWidth = 120 * pixelRatio;

		const djWidth = stageWidth * 0.25;
		const correctedWidth = Math.max(djWidth, minWidth);
		this.lottieRenderer.add({
			key: 'background-dj',
			signature: 'background-dj',
			x: stageWidth,
			y: stageHeight,
			width: correctedWidth,
			height: correctedWidth,
			animationData: djAnimation,
			depth: -650,
			originX: 1,
			originY: 1,
			isPlaying: true,
		});
		this.drawDjScore(
			score,
			stageWidth - correctedWidth * 0.5,
			stageHeight - correctedWidth * 0.3,
			correctedWidth,
			-650,
		);

		const speakerWidth = stageWidth * 0.2;
		const correctedSpeakerWidth = Math.max(speakerWidth, minWidth);
		const showInCorner = !showMovementButtons || viewportWidth <= viewportHeight;
		this.lottieRenderer.add({
			key: 'background-speaker',
			signature: 'background-speaker',
			x: 0,
			y: stageHeight * (showInCorner ? 0.99 : 0.66),
			width: correctedSpeakerWidth,
			height: correctedSpeakerWidth,
			animationData: speaker,
			depth: -700,
			originX: 0,
			originY: 1,
			isFlipped: true,
			isPlaying: true,
		});
	}

	private addImageByWidth(textureKey: string, x: number, y: number, width: number, depth: number) {
		const source = this.scene.textures.get(textureKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
		const imageWidth = source.width || width;
		const imageHeight = source.height || width;
		const height = width * (imageHeight / imageWidth);
		const image = this.scene.add.image(x, y, textureKey).setDisplaySize(width, height).setDepth(depth);

		this.addBoardObject(image);
	}

	private addImageByHeight(textureKey: string, x: number, y: number, height: number, depth: number) {
		const source = this.scene.textures.get(textureKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
		const imageWidth = source.width || height;
		const imageHeight = source.height || height;
		const width = height * (imageWidth / imageHeight);
		const image = this.scene.add.image(x, y, textureKey).setDisplaySize(width, height).setDepth(depth);

		this.addBoardObject(image);
	}

	private drawDjScore(score: number, x: number, y: number, djWidth: number, depth: number) {
		const text = this.scene.add
			.text(x, y, String(score), {
				fontFamily: 'ModernEra, Helvetica, Arial, sans-serif',
				fontSize: `${Math.round(djWidth * 0.19)}px`,
				fontStyle: '900',
				color: '#ffffff',
				align: 'center',
				resolution: LOTTIE_TEXTURE_SCALE,
			})
			.setOrigin(0.5)
			.setDepth(depth);

		this.addBoardObject(text);
	}
}
